import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import {
  appointmentStatusLabelKey,
} from '../../../utils/appointmentWorkflow';
import {
  useProfessionalClientSummaryQuery,
} from '../../../services/clientSummaryApi';

const Field = ({ label, value }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: '#8a6a54',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ color: '#2f241e', fontWeight: 700 }}>
      {value || '—'}
    </Typography>
  </Box>
);

export default function ProfessionalClientDetailsDialog({
  open,
  client,
  salonId,
  onClose,
}) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } =
    useProfessionalClientSummaryQuery(
      { clientId: client?.id, salonId },
      { skip: !open || !client?.id },
    );
  const locale = i18n.resolvedLanguage || i18n.language || 'fr';

  const formatDate = (value) => (
    value
      ? new Intl.DateTimeFormat(
        locale,
        { dateStyle: 'medium' },
      ).format(new Date(value))
      : '—'
  );
  const formatDateTime = (value) => (
    value
      ? new Intl.DateTimeFormat(
        locale,
        { dateStyle: 'medium', timeStyle: 'short' },
      ).format(new Date(value))
      : '—'
  );
  const attendanceLabel = (value) => {
    if (value === true) return t('pro_clients.details.present');
    if (value === false) return t('pro_clients.details.absent');
    return t('pro_clients.details.not_recorded');
  };
  const statusLabel = (status) => {
    if (status === 'reschedule_required') {
      return t('client.status.reschedule_required');
    }
    const key = appointmentStatusLabelKey(status);
    return key === status ? status : t(key);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 4,
          backgroundColor: '#fffdf9',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2.5, sm: 4 },
          py: 2.5,
        }}
      >
        <Typography
          component="span"
          variant="h5"
          sx={{ color: '#5e351f', fontWeight: 800 }}
        >
          {t('pro_clients.details.title')}
        </Typography>
        <IconButton aria-label={t('common.close')} onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, py: 2.5 }}>
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 320,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            {t('pro_clients.details.error')}
          </Alert>
        )}

        {data && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  borderColor: '#f0dfd0',
                  backgroundColor: '#fffaf4',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: '#5e351f', fontWeight: 800, mb: 2 }}
                >
                  {t('pro_clients.details.identity')}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: '#2f241e', fontWeight: 800, mb: 2 }}
                >
                  {data.client.first_name} {data.client.last_name}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <Field
                    label={t('pro_clients.columns.email')}
                    value={data.client.email}
                  />
                  <Field
                    label={t('pro_clients.columns.phone')}
                    value={data.client.phone}
                  />
                </Box>
                <Typography sx={{ mt: 2, color: '#8a6a54' }}>
                  {t('pro_clients.details.member_since', {
                    date: formatDate(data.client.created_at),
                  })}
                </Typography>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  borderColor: '#f0dfd0',
                  backgroundColor: '#fffaf4',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: '#5e351f', fontWeight: 800, mb: 2 }}
                >
                  {t('pro_clients.details.top_services')}
                </Typography>
                {data.top_services.length === 0 ? (
                  <Typography color="text.secondary">
                    {t('pro_clients.details.no_top_services')}
                  </Typography>
                ) : (
                  data.top_services.map((service, index) => (
                    <Box
                      key={service.service_id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1,
                      }}
                    >
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{
                          backgroundColor: '#f7dfc8',
                          color: '#7a3f1d',
                          fontWeight: 800,
                        }}
                      />
                      <Typography sx={{ fontWeight: 700 }}>
                        {service.service_name}
                      </Typography>
                      <Typography color="text.secondary">
                        {t('pro_clients.details.appointment_count', {
                          count: service.appointment_count,
                        })}
                      </Typography>
                    </Box>
                  ))
                )}
              </Paper>
            </Box>

            <Typography
              variant="h6"
              sx={{ color: '#5e351f', fontWeight: 800, mt: 3, mb: 1.5 }}
            >
              {t('pro_clients.details.history')}
            </Typography>

            <Box
              data-testid="client-appointment-history"
              style={{ height: 320, overflowY: 'auto' }}
              sx={{ pr: 1 }}
            >
              {data.recent_appointments.length === 0 ? (
                <Typography color="text.secondary">
                  {t('pro_clients.details.no_history')}
                </Typography>
              ) : (
                data.recent_appointments.map((appointment) => (
                  <Paper
                    key={appointment.appointment_id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 1.5,
                      borderRadius: 3,
                      borderColor: '#f0dfd0',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 1.5,
                      }}
                    >
                      <Typography sx={{ fontWeight: 800 }}>
                        {formatDateTime(appointment.start_time)}
                        {' – '}
                        {formatDateTime(appointment.end_time)}
                      </Typography>
                      <Chip
                        size="small"
                        label={statusLabel(appointment.status)}
                        sx={{
                          backgroundColor: '#f7dfc8',
                          color: '#7a3f1d',
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, 1fr)',
                          md: 'repeat(3, 1fr)',
                        },
                        gap: 1.5,
                      }}
                    >
                      <Field
                        label={t('pro_clients.details.salon')}
                        value={appointment.salon_name}
                      />
                      <Field
                        label={t('pro_clients.details.service')}
                        value={appointment.service_name}
                      />
                      <Field
                        label={t('pro_clients.details.employee')}
                        value={appointment.team_member_name}
                      />
                      <Field
                        label={t('pro_clients.details.price')}
                        value={
                          appointment.service_price == null
                            ? '—'
                            : `${appointment.service_price} DA`
                        }
                      />
                      <Field
                        label={t('pro_clients.details.attendance')}
                        value={attendanceLabel(
                          appointment.client_showed_up,
                        )}
                      />
                    </Box>
                    {appointment.cancellation_reason && (
                      <Alert severity="error" sx={{ mt: 1.5 }}>
                        <strong>
                          {t(
                            'pro_clients.details.cancellation_reason',
                          )} :
                        </strong>{' '}
                        {appointment.cancellation_reason}
                      </Alert>
                    )}
                    {appointment.reschedule_reason && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                        <strong>
                          {t(
                            'pro_clients.details.reschedule_reason',
                          )} :
                        </strong>{' '}
                        {appointment.reschedule_reason}
                      </Alert>
                    )}
                  </Paper>
                ))
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, py: 2 }}>
        <Button onClick={onClose} sx={{ color: '#7a3f1d', fontWeight: 700 }}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
